/**
 * The rounded "vs. last year" box at the end of an account row: "+6%",
 * "−14%", or "—" when there's nothing to compare against. Big changes are
 * filled green or orange; small ones stay grey so they don't draw the eye.
 */

import { growthWhole } from "../format";

/** A change this big or bigger, either way, gets a colored fill (0.05 = 5%). */
export const PILL_COLOR_THRESHOLD = 0.05;

// Float slack: a true 5% drop can compute just short of it ($19 → $18.05 gives −0.04999999999999993)
const EPSILON = 1e-9;

/** Fill color for a change: up, down, or flat (grey). */
export function pillTone(growth: number | null): "up" | "down" | "flat" {
  // No comparison: grey
  if (growth === null || !Number.isFinite(growth)) return "flat";
  // Up by at least the threshold
  if (growth >= PILL_COLOR_THRESHOLD - EPSILON) return "up";
  // Down by at least the threshold
  if (growth <= -PILL_COLOR_THRESHOLD + EPSILON) return "down";
  // A small change either way
  return "flat";
}

// Background for each tone; the text stays dark on all three
const FILL = {
  up: "bg-si-up-fill", // bright green
  down: "bg-si-down-fill", // bright orange
  flat: "bg-si-surface-2", // light grey
};

/**
 * Text size and side padding for the pill's text, so long values fit a pill
 * that never changes width: "+66%" at 13px, "+12,345%" at 12px,
 * ">100,000%" at 11px.
 */
export function pillTextSize(text: string): string {
  // Most values: the normal size
  if (text.length <= 7) return "px-[9px] text-si-sm";
  // One more character: a little smaller
  if (text.length === 8) return "px-[9px] text-[12px]";
  // Nine or more: smaller still, with less padding
  return "px-1 text-[11px]";
}

export function GrowthPill({ growth }: { growth: number | null }) {
  const text = growthWhole(growth);
  return (
    // Fixed width, the whole column, so every pill is the same size
    <span
      className={`inline-block w-[76px] justify-self-end whitespace-nowrap rounded-si-sm py-[5px] text-center font-semibold tabular-nums text-si-ink ${pillTextSize(text)} ${FILL[pillTone(growth)]}`}
    >
      {text}
    </span>
  );
}
